import { FC, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { RadioGroup, Text } from '@concepta/react-material-ui';
import { RadioOptions } from '@concepta/react-material-ui/dist/components/RadioGroup';
import {
  Form,
  FormContainer,
  LocationType,
  LocationMoment,
  AccessoriesFormErrors,
} from 'app/hooks/useGlobalForm';
import useGetResortData from 'app/hooks/useGetResortData';
import FormLabel from 'app/components/FormLabel';
import DatePicker from 'app/components/DatePicker';
import LocationResort from './LocationResort';
import LocationVacationHome from './LocationVacationHome';
import LocationTime from './LocationTime';
import LocationSameAsCheckin from './LocationSameAsCheckin';
import useLocalStorageState from 'use-local-storage-state';
import {
  ProductCategoryInterface,
  ResortInterface,
  SystemParameterInterface,
} from 'ks-common';
import ErrorText from 'app/components/ErrorText';
import {
  AccessoriesScreenContainer,
  Instructions,
} from 'app/hooks/useAccessoriesScreenState';
import { isMobile } from 'utils/isMobile';
import isToday from 'date-fns/isToday';
import { DateObject } from 'react-multi-date-picker';
import LocationWarnings from './LocationWarnings';
import BlueWrapper from 'app/components/BlueWrapper';
import ProductCategoryRestrictionModal from 'app/components/ProductCategoryRestrictionModal';

// TODO get constants from ks-common (Got help from Marshall. This is not working now and we don't know why)
const DELIVERY_TIME_PREMIUM_FEE = '06:00 AM';

/**
 * Validates if airport delivery/pickup is allowed for the current product category type.
 * Category types 3 and 5 are not allowed to use airport delivery/pickup.
 * @param categoryType - The Type value from the current product's category
 * @returns true if airport is NOT allowed (should be disabled), false if airport is allowed
 */
const isAirportDisabledForCategoryType = (
  categoryType?: number | null,
): boolean => {
  return !!(categoryType && [3, 5].includes(categoryType));
};

type OriginalResorts = Partial<
  Record<LocationMoment, ResortInterface | null | undefined>
>;

interface Props {
  id: LocationMoment;
  formErrors?: Partial<AccessoriesFormErrors>;
  zipError?: string;
  dateError?: string;
  originalResorts?: OriginalResorts;
}

const Location: FC<Props> = ({
  id,
  formErrors,
  zipError,
  dateError,
  originalResorts,
}) => {
  const [systemParams] =
    useLocalStorageState<SystemParameterInterface[]>('systemParams');
  const { currentProduct, updateCurrentProduct } = FormContainer.useContainer();
  const {
    deliveryInstructions,
    returnInstructions,
    setDeliveryInstructions,
    setReturnInstructions,
    setDeliveryRestriction,
    setReturnRestriction,
  } = AccessoriesScreenContainer.useContainer();

  const isDelivery = id === LocationMoment.DeliveryLocation;
  const otherId = isDelivery
    ? LocationMoment.ReturnLocation
    : LocationMoment.DeliveryLocation;
  const setRestrictionStatus = isDelivery
    ? setDeliveryRestriction
    : setReturnRestriction;

  const isReturn = id === 'returnLocation';
  const location = currentProduct[id]?.location;
  const locationTime = currentProduct[id]?.time;

  const isResort = location === LocationType.Resort;
  const isVacationHome = location === LocationType.VacationHome;
  const isAirport = location === LocationType.Airport;

  const formResort = currentProduct[id]?.resort;
  const formResortId = formResort?.ResortId;

  const resortId = useMemo(() => {
    if (isAirport) {
      return 8415;
    }
    if (isVacationHome) {
      return 243;
    }

    return formResortId;
  }, [location, formResortId]);

  const {
    data: resortData,
    execute,
    isPending,
    error,
  } = useGetResortData(resortId);

  const locationError = formErrors?.[id];

  const productCategory = currentProduct?.product?.productCategory;
  const categoryType = productCategory?.Type;
  const productCategoryId = currentProduct?.product?.ProductCategoryId;
  const allowedCategories: ProductCategoryInterface[] =
    resortData?.AllowedProductCategories ?? [];
  const isCategoryAllowed =
    !productCategoryId ||
    allowedCategories.some(c => c.ProductCategoryId === productCategoryId);

  const isBlocked =
    isResort &&
    !!formResortId &&
    resortData?.ResortId === formResortId &&
    !isCategoryAllowed;

  const [restrictionOpen, setRestrictionOpen] = useState(false);
  const [blockedSnapshot, setBlockedSnapshot] = useState<{
    resortName: string;
    categoryName: string;
  } | null>(null);

  useEffect(() => {
    if (resortId) {
      execute();
    }
  }, [location, resortId]);

  useEffect(() => {
    setRestrictionStatus({
      isPending: isResort && !!formResortId && isPending,
      isBlocked,
    });
    return () => {
      setRestrictionStatus({ isPending: false, isBlocked: false });
    };
  }, [isResort, formResortId, isPending, isBlocked]);

  // Capture latest revert context in a ref so the effect below can react to
  // `isBlocked` transitions only, without going stale on props/state that
  // resolve after mount (e.g. the edit-order snapshot landing late).
  const revertCtxRef = useRef({
    currentProduct,
    formResortId,
    originalResorts,
    updateCurrentProduct,
    id,
    otherId,
  });
  revertCtxRef.current = {
    currentProduct,
    formResortId,
    originalResorts,
    updateCurrentProduct,
    id,
    otherId,
  };

  useEffect(() => {
    if (!isBlocked) return;
    setBlockedSnapshot({
      resortName: resortData?.ResortName ?? '',
      categoryName: productCategory?.CategoryName ?? 'this',
    });
    setRestrictionOpen(true);

    // In edit-order (and other re-editing flows) AccessoriesForm passes the
    // pre-edit resort snapshot — revert to it instead of clearing. In new-order
    // flow `originalResorts` is undefined and we fall through to null. If the
    // snapshot itself happens to be blocked, a follow-up `isBlocked` cycle
    // clears it to null (the `ResortId !== formResortId` guard prevents
    // reverting back to the same blocked resort).
    const ctx = revertCtxRef.current;

    const resolveRevert = (moment: LocationMoment) => {
      const original = ctx.originalResorts?.[moment];
      if (!original || original.ResortId === ctx.formResortId) return null;
      return original;
    };

    const updates: Partial<Form> = {
      [ctx.id]: { resort: resolveRevert(ctx.id) },
    };
    if (
      ctx.currentProduct?.[ctx.otherId]?.resort?.ResortId === ctx.formResortId
    ) {
      updates[ctx.otherId] = { resort: resolveRevert(ctx.otherId) };
    }
    ctx.updateCurrentProduct(updates);
  }, [isBlocked]);

  const handleRestrictionClose = () => {
    setRestrictionOpen(false);
  };

  const onLocationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateCurrentProduct({
      [id]: {
        location: event.target.value,
      },
    });
  };

  const options = [
    {
      label: 'Resort',
      value: 'resort',
    },
    {
      label: 'Vacation Home',
      value: 'vacationHome',
    },
    {
      label: 'Airport (MCO)',
      value: 'airport',
      disabled: isAirportDisabledForCategoryType(categoryType),
    },
  ];

  useEffect(() => {
    if (!location) {
      updateCurrentProduct({
        [id]: {
          location: options[0].value,
        },
      });
    }
    if (
      location &&
      location === 'airport' &&
      isAirportDisabledForCategoryType(categoryType)
    ) {
      updateCurrentProduct({
        [id]: {
          location: options[0].value,
        },
      });
    }
  }, []);

  const disneyInstructionMessages = {
    deliveryLocation: systemParams?.find(
      param => param.ParamName === 'Disney_Delivery_Message',
    )?.ParamValue,
    returnLocation: systemParams?.find(
      param => param.ParamName === 'Disney_Pickup_Message',
    )?.ParamValue,
  };

  const isDisney = Number(formResort?.ResortType) === 1;

  const disneyInstructions = isDisney && disneyInstructionMessages[id];

  const currentResortData =
    resortData?.ResortId === resortId ? resortData : undefined;

  const resortIntructionsMessages = {
    deliveryLocation: currentResortData?.WarningDeliveryText,
    returnLocation: currentResortData?.WarningPickupText,
  };

  const resortIntructions = resortIntructionsMessages[id];

  const timeError = {
    deliveryLocation: formErrors?.deliveryTime,
    returnLocation: formErrors?.returnTime,
  };

  const title = `Select ${
    id === 'deliveryLocation' ? 'Delivery' : 'Return'
  } Location and Time`;

  const resortError = {
    deliveryLocation: formErrors?.deliveryLocationResort,
    returnLocation: formErrors?.returnLocationResort,
  };

  const vacationHomeError = {
    deliveryLocation: formErrors?.deliveryVacationHomeFields,
    returnLocation: formErrors?.returnVacationHomeFields,
  };

  useEffect(() => {
    const instructions: Instructions = {};

    if (isResort && disneyInstructions) {
      instructions.disneyInstructions = disneyInstructions;
    }

    if (resortId && (isResort || isAirport) && resortIntructions) {
      instructions.resortInstructions = resortIntructions;
    }

    id === 'deliveryLocation'
      ? setDeliveryInstructions(instructions)
      : setReturnInstructions(instructions);
  }, [resortId, resortIntructions]);

  const locationFeeTime =
    id === 'deliveryLocation' && isResort && DELIVERY_TIME_PREMIUM_FEE;

  const deliveryDateIsToday =
    !isReturn &&
    !!currentProduct?.deliveryDate &&
    isToday(
      new DateObject({
        date: currentProduct.deliveryDate,
        format: 'YYYY-MM-DD',
      }).toDate(),
    );

  const instructions =
    id === 'deliveryLocation' ? deliveryInstructions : returnInstructions;

  // const hasInstructions = !!Object.values(instructions).filter(i => !!i).length;
  const hasInstructions = !!Object.values(instructions).length;

  return (
    <Box>
      <FormLabel subLabel mt={isReturn ? 4 : 1} mb={1}>
        {title}
      </FormLabel>

      {isReturn && <LocationSameAsCheckin />}

      <RadioGroup
        options={options as RadioOptions[]}
        onChange={onLocationChange}
        value={location}
        row={!isMobile}
      />
      {locationError && <ErrorText>{locationError}</ErrorText>}

      {isResort && (
        <LocationResort
          id={id}
          error={resortError[id]}
          location={location}
          loading={
            !!formResortId && isPending && resortData?.ResortId !== formResortId
          }
        />
      )}

      {isVacationHome && (
        <LocationVacationHome
          id={id}
          error={zipError}
          fieldsErrors={vacationHomeError[id]}
        />
      )}

      {isAirport && (
        <>
          <Text
            mt={1}
            fontFamily="Roboto, Helvetica, Arial, sans-serif"
            fontSize="16px"
            fontWeight={400}
            color="grey.700"
          >
            Orlando International Airport - MCO
          </Text>
          <Divider sx={{ background: 'grey.200', mt: '4px' }} />
        </>
      )}

      {location && (
        <Box
          display="flex"
          alignItems="flex-start"
          mt={1}
          maxWidth={!isMobile ? '380px' : '100%'}
        >
          {!isMobile && (
            <Box flex={1}>
              <DatePicker
                type={isReturn ? 'return' : 'delivery'}
                surface="light"
              />
            </Box>
          )}
          <Box flex={1} {...(!isMobile && { ml: 4 })}>
            <LocationTime
              id={id}
              disabled={isPending || !!error || !resortData}
              resort={resortData}
              error={timeError[id]}
            />
          </Box>
        </Box>
      )}

      {!isMobile && deliveryDateIsToday && (
        <ErrorText>Please review and enter a valid delivery date.</ErrorText>
      )}

      {!isMobile && timeError[id] && <ErrorText>{timeError[id]}</ErrorText>}

      {isResort &&
        locationTime === locationFeeTime &&
        formResort?.ResortType !== 1 && (
          <ErrorText>
            A $10.00 premium will be applied for 6am deliveries.
          </ErrorText>
        )}

      {dateError && <ErrorText>{dateError}</ErrorText>}

      {isMobile && hasInstructions && (
        <BlueWrapper boxProps={{ mt: 4 }}>
          <LocationWarnings id={id} />
        </BlueWrapper>
      )}

      <ProductCategoryRestrictionModal
        open={restrictionOpen}
        onClose={handleRestrictionClose}
        locationMoment={id}
        resortName={blockedSnapshot?.resortName ?? ''}
        categoryName={blockedSnapshot?.categoryName ?? 'this'}
      />
    </Box>
  );
};

export default Location;
